/* eslint-disable */
/**
 * ModalSkal — Mørk overlay, lukkes med Esc, focus-trap
 *
 * Props:
 *   åben      bool
 *   onLuk     fn
 *   titel     string
 *   børn      ReactNode (children)
 *   bredde    number  — max-width i px (default 560)
 *   style     obj     — ekstra styles til modal-boksen
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { BG, KANT, TEKST, RADIUS, FONT } from './tokens';

export default function ModalSkal({ åben, onLuk, titel, children, bredde = 560, style = {} }) {
  const modalRef = useRef(null);

  // Luk ved Esc
  const handleKey = useCallback(e => {
    if (e.key === 'Escape') onLuk?.();
  }, [onLuk]);

  // Focus-trap: gem og gendan fokus
  useEffect(() => {
    if (!åben) return;
    const prev = document.activeElement;
    const t = setTimeout(() => { modalRef.current?.focus(); }, 50);
    document.addEventListener('keydown', handleKey);
    return () => {
      clearTimeout(t);
      document.removeEventListener('keydown', handleKey);
      prev?.focus();
    };
  }, [åben, handleKey]);

  // Forhindre scroll i body når modal er åben
  useEffect(() => {
    document.body.style.overflow = åben ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [åben]);

  if (!åben) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position:   'fixed',
        inset:      0,
        zIndex:     1000,
        display:    'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: BG.overlay,
        padding:    '16px',
        fontFamily: FONT.family,
      }}
      onClick={e => { if (e.target === e.currentTarget) onLuk?.(); }}
    >
      <div
        ref={modalRef}
        tabIndex={-1}
        style={{
          background:   'rgba(8,14,34,0.97)',
          border:       `1px solid ${KANT.aktiv}`,
          borderRadius: RADIUS.kort + 2,
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          width:        '100%',
          maxWidth:     bredde,
          maxHeight:    '90vh',
          display:      'flex',
          flexDirection: 'column',
          outline:      'none',
          boxShadow:    '0 24px 64px rgba(0,0,0,0.55)',
          ...style,
        }}
      >
        {/* Header */}
        <div style={{
          display:    'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding:    '16px 20px',
          borderBottom: `1px solid ${KANT.standard}`,
          flexShrink: 0,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: TEKST.primær }}>
            {titel}
          </div>
          <button
            onClick={onLuk}
            aria-label="Luk"
            style={{
              background:   'none',
              border:       `1px solid ${KANT.standard}`,
              borderRadius: 6,
              color:        TEKST.sekundær,
              fontSize:     16,
              lineHeight:   1,
              padding:      '3px 9px',
              cursor:       'pointer',
            }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = KANT.aktiv; e.currentTarget.style.color = TEKST.primær; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = KANT.standard; e.currentTarget.style.color = TEKST.sekundær; }}
          >
            ×
          </button>
        </div>

        {/* Indhold — scrollbart */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
          {children}
        </div>
      </div>
    </div>
  );
}
